import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AccountTransactionDetailDocument,
  CategorizeBankingTransactionDocument,
  CustomCategoryForSelectFragment,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import PageLoading from 'modules/layout/components/Page/Loading'

import { TransactionDetailComponent } from './TransactionDetailComponent'

const TransactionDetailSidePanel = () => {
  const { transaction_id: transactionId, id } = useParams<{ transaction_id: string; id: string }>()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { loading, data } = useQuery(AccountTransactionDetailDocument, {
    variables: {
      companyId: company.id,
      id: String(transactionId),
    },
    skip: !transactionId || !company.id,
    onCompleted: (completedData) => {
      if (completedData?.banking?.transaction?.category) {
        setCategory(completedData?.banking?.transaction?.category)
      }
    },
  })

  const transaction = data?.banking.transaction
  const [category, setCategory] = useState<CustomCategoryForSelectFragment | null | undefined>(
    transaction?.category
  )

  const [categorizeTransaction] = useMutation(CategorizeBankingTransactionDocument, {
    extractorFn: (updatedData) => updatedData.bankingMutations.categorizeBankingTransaction,
  })

  const onChangeCategory = async (newCategory?: CustomCategoryForSelectFragment | null) => {
    if (newCategory?.id === category?.id) return

    const result = await categorizeTransaction({
      variables: {
        id: String(transactionId),
        companyId: company.id,
        categoryId: newCategory?.id,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      setCategory(newCategory)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    }
  }

  if (!id) return <NotFound />

  const canCategorizeTransaction = data?.banking?._policies?.transactionCategorize?.can ?? false
  const canManageJournalEntries = data?.finance?._policies?.journalEntriesManage?.can ?? false

  const backUrl = navigation.finance.banking.accounts.accountId.transactions.fullPath({ id })
  const documentUrl = transaction?.proofOfTransfer?.url

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={i18n.t('expenses_v2.transactions.detail.title')}
      open
      onClose={() => navigate(backUrl)}
      headerBorder
      closeLabel={i18n.t('common.close')}
      locked
      footer={
        documentUrl && (
          <Button.Router
            type="secondary"
            to={documentUrl}
            rel="noopener noreferrer"
            iconName="download"
            blankTarget
            download
          >
            {i18n.t('expenses_v2.transactions.detail.actions.download')}
          </Button.Router>
        )
      }
    >
      {loading && <PageLoading />}
      {!loading && !transaction && <NotFound />}
      {!loading && !!transaction && (
        <TransactionDetailComponent
          transaction={transaction}
          canCategorizeTransaction={canCategorizeTransaction}
          canManageJournalEntries={canManageJournalEntries}
          currentCategory={category}
          onChangeCategory={onChangeCategory}
        />
      )}
    </Sidepanel>
  )
}

export default TransactionDetailSidePanel
